import { useEffect, useState } from "react";
import QRCode from "qrcode";
import {
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { AppModal } from "global/components/AppModal";
import { fetchQRCodeForMFA, verifyMFACode } from "../SettingsService";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { CustomInput } from "global/components";
import { MuiOtpInput } from "mui-one-time-password-input";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface CustomProps {
  handleDialogClose: Function;
  visible: boolean;
  updateMFAStatus: Function;
  mfaEnabled: boolean;
}

const ManageMFAModal = (props: CustomProps) => {
  const disableMFA = props.mfaEnabled;
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const [qr, setQR] = useState<string>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [credentials, setCredentials] = useState<{
    [key: string]: {
      value: string;
      error: string;
    };
  }>({
    code: {
      value: "",
      error: "",
    },
    password: {
      value: "",
      error: "",
    },
  });
  const [visibility, setVisibility] = useState(false);

  useEffect(() => {
    if (props.visible) {
      makeQRCode();
    }
  }, [props.visible]);

  const makeQRCode = async () => {
    try {
      const response = await fetchQRCodeForMFA();
      await generateQRCodeImage(response.message);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleValidate = () => {
    let errors = credentials;
    let isValid: boolean = true;
    const password = credentials.password.value;
    const verificationCode = credentials.code.value;
    if (!verificationCode && !password) {
      errors.code.error = "Please enter TOTP";
      errors.password.error = "Please enter password";
      isValid = false;
    }
    if (!verificationCode) {
      errors.code.error = "Please enter TOTP";
      isValid = false;
    }
    if (!password) {
      errors.password.error = "Please enter password";
      isValid = false;
    }
    setCredentials({ ...errors });
    return isValid;
  };

  const handleValidateForDisableMFA = () => {
    let errors = credentials;
    let isValid: boolean = true;
    const password = credentials.password.value;
    if (!password) {
      errors.password.error = "Please enter password";
      isValid = false;
    }
    setCredentials({ ...errors });
    return isValid;
  };

  const updateMFA = async () => {
    try {
      if (disableMFA ? handleValidateForDisableMFA() : handleValidate()) {
        setProcessing(true);
        const response = await verifyMFACode(
          credentials.password.value,
          credentials.code.value,
          !disableMFA
        );
        props.updateMFAStatus(!disableMFA);
        openSuccessNotification(response.message);
        handleClose();
        setProcessing(false);
      }
    } catch (error: any) {
      setProcessing(false);
      handleErrorMessage(error);
    }
  };

  const generateQRCodeImage = async (url: string) => {
    try {
      const qrCodeImage = await QRCode.toDataURL(url);
      setQR(qrCodeImage);
    } catch (error: any) {
      openErrorNotification(error);
    }
  };

  const getBody = () => {
    return (
      <Stack direction="column" spacing={2} pt={2}>
        <Typography variant="body2">
          {disableMFA
            ? "Please enter your password to disable the multi-factor authentication."
            : "To enable the multi-factor authentication, you need to scan this QR Code with your Authentication App and enter the verification code and your password below."}
        </Typography>
        {!disableMFA && (
          <Stack
            alignItems="center"
            sx={{ border: "0.5px solid #E9E9E9", borderRadius: "20px", py: 2 }}
          >
            <img src={qr} height={150} width={150} alt="QR code for two-factor authentication setup" />
          </Stack>
        )}
        <Box>
          {!disableMFA && (
            <InputLabel required htmlFor="password">
              Password
            </InputLabel>
          )}
          <CustomInput
            id="password"
            type={visibility ? "text" : "password"}
            placeHolder="Password"
            propsToInputElement={{ "aria-label": "Password" }}
            sx={{
              "& .MuiInputBase-input": { padding: "13px 13px" },
              "& input: : -ms-reveal": { display: "none" },
            }}
            onChange={(event: any) =>
              setCredentials({
                ...credentials,
                password: {
                  value: event.target.value,
                  error: "",
                },
              })
            }
            value={credentials.password.value}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={visibility ? "Hide password" : "Show password"}
                    aria-pressed={visibility}
                    onClick={() => setVisibility(!visibility)}
                    edge="end"
                  >
                    {visibility ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={
              credentials.password.value.length < 8
                ? credentials.password.error
                : null
            }
            helperText={credentials.password.error}
          />
        </Box>
        {!disableMFA && (
          <Stack spacing={1}>
            <InputLabel required id="mfa-otp-label">
              Verification Code
            </InputLabel>
            <Box role="group" aria-labelledby="mfa-otp-label">
              <MuiOtpInput
                length={6}
              value={credentials.code.value}
              onChange={(value: any) =>
                setCredentials({
                  ...credentials,
                  code: {
                    value: value.replace(/[^0-9]/g, ""),
                    error: "",
                  },
                })
              }
              sx={{
                "& .MuiFilledInput-input": {
                  p: isDesktop ? "14px" : "10px",
                },
                gap: isDesktop ? "20px" : "8px",
                "& .MuiFilledInput-root": {
                  width: isDesktop ? "auto" : "32px",
                  minWidth: isDesktop ? "auto" : "32px",
                },
              }}
              TextFieldsProps={{
                type: "number",
                inputProps: {
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  style: {
                    MozAppearance: "textfield",
                  },
                },
                sx: {
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                },
              }}
              />
            </Box>
            {!isTruthy(credentials.code.value) && (
              <FormHelperText error role="alert" sx={{ paddingLeft: "14px" }}>
                {credentials.code.error}
              </FormHelperText>
            )}
          </Stack>
        )}
      </Stack>
    );
  };

  const handleClose = () => {
    setCredentials({
      code: {
        value: "",
        error: "",
      },
      password: {
        value: "",
        error: "",
      },
    });
    props.handleDialogClose();
  };

  return (
    <AppModal
      open={props.visible}
      onClose={handleClose}
      title="Multi Factor Authentication"
      // Disabling MFA weakens the account, so that variant reads destructive.
      tone={disableMFA ? "destructive" : "neutral"}
      size="sm"
      busy={processing}
      primaryLabel={disableMFA ? "Disable" : "Verify"}
      onPrimary={updateMFA}
      primaryLoading={processing}
      hideCancel
    >
      {getBody()}
    </AppModal>
  );
};

export default ManageMFAModal;
